import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { organizationsApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  ClipboardIcon,
  ArrowPathIcon,
  CodeBracketIcon,
  LinkIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';

export default function OrgIntegration() {
  const { currentOrganization } = useAuthStore();
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://api.dev.venndela.com';

  const handleRegenerateApiKey = async () => {
    if (!currentOrganization) return;
    if (!confirm('Are you sure? This will invalidate your current API key.')) return;

    setIsRegenerating(true);
    try {
      const response = await organizationsApi.regenerateApiKey(currentOrganization.organizationId);
      if (response.data.success) {
        setApiKey(response.data.data.apiKey);
        toast.success('API key regenerated');
      }
    } catch {
      toast.error('Failed to regenerate API key');
    } finally {
      setIsRegenerating(false);
    }
  };

  const copyToClipboard = (text: string, section: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const trackingSnippet = `<!-- Venndela Affiliate Tracking (Optional - for URL parameter capture) -->
<script>
(function() {
  const urlParams = new URLSearchParams(window.location.search);
  const ref = urlParams.get('ref');
  if (ref) {
    document.cookie = 'VennDela_ref=' + ref + '; path=/; max-age=' + (30 * 24 * 60 * 60);
  }
})();
</script>`;

  const conversionCode = `// Record a conversion when a user signs up or completes an action
// Call this from your backend when a customer registers

const response = await fetch('${apiBaseUrl}/t/conversion', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Api-Key': 'YOUR_API_KEY' // Replace with your actual API key
  },
  body: JSON.stringify({
    customerId: 'customer_123',        // Your unique customer ID (required)
    customerEmail: 'user@example.com', // Customer email (optional)
    visitorId: req.cookies.VennDela_vid     // From the VennDela_vid cookie (optional)
  })
});`;

  const saleCode = `// Record a sale when a purchase is completed
// Call this from your backend when payment is successful

const response = await fetch('${apiBaseUrl}/t/sale', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Api-Key': 'YOUR_API_KEY' // Replace with your actual API key
  },
  body: JSON.stringify({
    orderId: 'order_456',              // Your unique order ID (required)
    amount: 9999,                       // Amount in cents - $99.99 (required)
    customerId: 'customer_123',        // Your customer ID (required)
    customerEmail: 'user@example.com', // Customer email (optional)
    visitorId: req.cookies.VennDela_vid,    // From the VennDela_vid cookie (optional)
    campaignId: 'campaign_id'          // Specific campaign (optional)
  })
});`;

  const curlConversionExample = `curl -X POST ${apiBaseUrl}/t/conversion \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -d '{
    "customerId": "customer_123",
    "customerEmail": "user@example.com"
  }'`;

  const curlSaleExample = `curl -X POST ${apiBaseUrl}/t/sale \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -d '{
    "orderId": "order_456",
    "amount": 9999,
    "customerId": "customer_123"
  }'`;

  const getLLMInstructions = () => {
    return `# Venndela Integration Guide

## Overview
I need to integrate affiliate tracking into my website/application. The system uses a cookie-based tracking approach where affiliates share tracking links, and I need to record sales from my backend.

## My Configuration
- API Base URL: ${apiBaseUrl}
- Organization: ${currentOrganization?.name || 'My Organization'}
${apiKey ? `- API Key: ${apiKey}` : '- API Key: [Generate from dashboard]'}

## How Tracking Works
1. Affiliates create tracking links in their dashboard
2. Tracking link format: ${apiBaseUrl}/t/[shortCode]
3. When a visitor clicks a tracking link, they are redirected to my site with tracking cookies set automatically
4. Cookies set by the system:
   - VennDela_vid: Visitor ID
   - VennDela_aid: Affiliate ID
   - VennDela_lid: Link ID
   - VennDela_oid: Organization ID
5. When I record a sale, the system uses these cookies to attribute it to the correct affiliate

## API Endpoints

### Record Conversion (Optional)
Use this when a referred visitor completes a signup or registration.

**Endpoint:** POST ${apiBaseUrl}/t/conversion

**Headers:**
- Content-Type: application/json
- X-Api-Key: YOUR_API_KEY

**Request Body:**
\`\`\`json
{
  "customerId": "your_customer_id",     // Required: Your unique customer identifier
  "customerEmail": "user@example.com",  // Optional: Customer email
  "visitorId": "from_VennDela_vid_cookie"    // Optional: From VennDela_vid cookie for better attribution
}
\`\`\`

### Record Sale (Required)
Use this when a purchase is completed to calculate and record the commission.

**Endpoint:** POST ${apiBaseUrl}/t/sale

**Headers:**
- Content-Type: application/json
- X-Api-Key: YOUR_API_KEY

**Request Body:**
\`\`\`json
{
  "orderId": "your_order_id",           // Required: Your unique order identifier
  "amount": 9999,                        // Required: Amount in CENTS (9999 = $99.99)
  "customerId": "your_customer_id",     // Required: Your unique customer identifier
  "customerEmail": "user@example.com",  // Optional: Customer email
  "visitorId": "from_VennDela_vid_cookie",   // Optional: From VennDela_vid cookie for better attribution
  "campaignId": "specific_campaign_id"  // Optional: Override which campaign to use
}
\`\`\`

## Code Examples

### JavaScript/Node.js - Record Sale
\`\`\`javascript
${saleCode}
\`\`\`

### JavaScript/Node.js - Record Conversion
\`\`\`javascript
${conversionCode}
\`\`\`

### cURL - Record Sale
\`\`\`bash
${curlSaleExample}
\`\`\`

### cURL - Record Conversion
\`\`\`bash
${curlConversionExample}
\`\`\`

## Optional: URL Parameter Tracking
If you also want to capture ?ref=CODE parameters from URLs, add this script to your site:

\`\`\`html
${trackingSnippet}
\`\`\`

## API Parameters Reference

| Parameter | Type | Required | Description |
|-----------|------|----------|-------------|
| orderId | string | Yes (sale) | Your unique order identifier |
| amount | number | Yes (sale) | Order amount in cents (e.g., 9999 = $99.99) |
| customerId | string | Yes | Your unique customer identifier |
| customerEmail | string | No | Customer email address |
| visitorId | string | No | From VennDela_vid cookie (improves attribution) |
| campaignId | string | No | Override campaign for this sale |

## Important Notes
- Always call the API from your backend server, never from the frontend (to protect your API key)
- The amount must be in cents (multiply dollars by 100)
- The visitorId from the VennDela_vid cookie improves attribution accuracy
- Test your integration in development before going live

Please help me implement this integration in my application.`;
  };

  const copyForLLM = () => {
    const text = getLLMInstructions();
    navigator.clipboard.writeText(text);
    setCopiedSection('llm');
    toast.success('Integration guide copied for LLM');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-charcoal">Integration Guide</h2>
          <p className="text-charcoal/60 mt-1">
            Follow these steps to integrate affiliate tracking into your website
          </p>
        </div>
        <button
          onClick={copyForLLM}
          className="btn btn-secondary flex items-center gap-2"
        >
          {copiedSection === 'llm' ? (
            <CheckCircleIcon className="w-4 h-4 text-sage" />
          ) : (
            <DocumentDuplicateIcon className="w-4 h-4" />
          )}
          Copy for LLM
        </button>
      </div>

      {/* Quick Start Steps */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">Quick Start</h3>
          <p className="text-sm text-charcoal/60 mt-1">Just 2 steps to start tracking affiliate commissions</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-rust rounded-full flex items-center justify-center text-white font-bold">
                1
              </div>
              <div>
                <h4 className="font-medium text-charcoal">Connect Stripe & Generate API Key</h4>
                <p className="text-sm text-charcoal/60">Connect your Stripe account in Settings, then generate an API key below</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-rust rounded-full flex items-center justify-center text-white font-bold">
                2
              </div>
              <div>
                <h4 className="font-medium text-charcoal">Pass tracking cookies to Stripe</h4>
                <p className="text-sm text-charcoal/60">
                  Add VennDela tracking cookies to your Stripe checkout metadata. Commissions are calculated automatically.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 1: API Key */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30 flex items-center gap-2">
          <CodeBracketIcon className="w-5 h-5 text-rust" />
          <h3 className="text-lg font-display font-semibold text-charcoal">Step 1: Generate API Key</h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-charcoal/60">
            Your API key authenticates your integration. Keep it secure and only use it from your backend server.
          </p>

          {apiKey ? (
            <div className="p-4 bg-cream rounded-lg border border-tan/30">
              <p className="text-xs text-charcoal/60 mb-2">Your API Key (save this - it won't be shown again)</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sage font-mono text-sm break-all">{apiKey}</code>
                <button
                  onClick={() => copyToClipboard(apiKey, 'apiKey')}
                  className="btn btn-secondary btn-sm"
                >
                  {copiedSection === 'apiKey' ? (
                    <CheckCircleIcon className="w-4 h-4 text-sage" />
                  ) : (
                    <ClipboardIcon className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <button
                onClick={handleRegenerateApiKey}
                disabled={isRegenerating}
                className="btn btn-primary"
              >
                <ArrowPathIcon className="w-4 h-4 mr-2" />
                {isRegenerating ? 'Generating...' : 'Generate API Key'}
              </button>
              <span className="text-sm text-charcoal/60">
                Click to generate a new API key
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Step 2: Stripe Integration (Essential) */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30 flex items-center gap-2">
          <svg className="w-5 h-5 text-[#635BFF]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
          </svg>
          <h3 className="text-lg font-display font-semibold text-charcoal">
            Step 2: Pass Tracking Data to Stripe
          </h3>
        </div>
        <div className="p-6 space-y-6">
          <p className="text-sm text-charcoal/60">
            When customers click affiliate links, VennDela sets cookies in their browser.
            Pass these cookies to Stripe so payments are attributed to the correct affiliate.
          </p>

          {/* Client-side cookie reading */}
          <div className="space-y-2">
            <p className="text-xs text-charcoal/50 font-medium">A. Read cookies (client-side JavaScript)</p>
            <div className="relative">
              <pre className="bg-charcoal rounded p-3 text-xs text-cream overflow-x-auto">
{`// Helper function to read VennDela tracking cookies
function getVennDelaCookies() {
  const getCookie = (name) => {
    const value = \`; \${document.cookie}\`;
    const parts = value.split(\`; \${name}=\`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  return {
    visitorId: getCookie('VennDela_vid'),
    affiliateId: getCookie('VennDela_aid'),
  };
}

// When starting checkout, send these to your server:
const tracking = getVennDelaCookies();
fetch('/api/create-checkout', {
  method: 'POST',
  body: JSON.stringify({ priceId, ...tracking })
});`}
              </pre>
              <button
                onClick={() => copyToClipboard(`function getVennDelaCookies() {
  const getCookie = (name) => {
    const value = \`; \${document.cookie}\`;
    const parts = value.split(\`; \${name}=\`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };
  return {
    visitorId: getCookie('VennDela_vid'),
    affiliateId: getCookie('VennDela_aid'),
  };
}`, 'cookieHelper')}
                className="absolute top-2 right-2 p-1.5 text-cream/50 hover:text-cream hover:bg-white/10 rounded transition-colors"
              >
                {copiedSection === 'cookieHelper' ? (
                  <CheckCircleIcon className="w-4 h-4 text-sage" />
                ) : (
                  <ClipboardIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Server-side Stripe call */}
          <div className="space-y-2">
            <p className="text-xs text-charcoal/50 font-medium">B. Pass to Stripe checkout (server-side)</p>
            <div className="relative">
              <pre className="bg-charcoal rounded p-3 text-xs text-cream overflow-x-auto">
{`// Stripe Checkout Session
const session = await stripe.checkout.sessions.create({
  mode: 'subscription', // or 'payment'
  line_items: [{ price: priceId, quantity: 1 }],
  success_url: 'https://yoursite.com/success',
  cancel_url: 'https://yoursite.com/cancel',

  // REQUIRED: Pass VennDela tracking data
  metadata: {
    VennDela_vid: visitorId || '',   // From cookie
    VennDela_aid: affiliateId || '', // From cookie
  },
});`}
              </pre>
              <button
                onClick={() => copyToClipboard(`metadata: {
  VennDela_vid: visitorId || '',
  VennDela_aid: affiliateId || '',
}`, 'stripeMetadata')}
                className="absolute top-2 right-2 p-1.5 text-cream/50 hover:text-cream hover:bg-white/10 rounded transition-colors"
              >
                {copiedSection === 'stripeMetadata' ? (
                  <CheckCircleIcon className="w-4 h-4 text-sage" />
                ) : (
                  <ClipboardIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Success message */}
          <div className="bg-sage/10 border border-sage/30 rounded-lg p-4">
            <p className="text-sm text-charcoal/80">
              <strong>That's it!</strong> Commissions are calculated automatically when Stripe payments succeed.
              Your affiliates will see their earnings in their dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* Advanced Options Dropdown */}
      <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full px-6 py-4 border-b border-tan/30 flex items-center justify-between hover:bg-cream/30 transition-colors"
        >
          <div className="flex items-center gap-2">
            <CodeBracketIcon className="w-5 h-5 text-charcoal/60" />
            <h3 className="text-lg font-display font-semibold text-charcoal">Advanced Options</h3>
            <span className="text-xs text-charcoal/50 ml-2">Manual API, URL tracking, attribution details</span>
          </div>
          {showAdvanced ? (
            <ChevronUpIcon className="w-5 h-5 text-charcoal/60" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-charcoal/60" />
          )}
        </button>

        {showAdvanced && (
          <div className="p-6 space-y-8">
            {/* How Tracking Works */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-rust" />
                <h4 className="font-medium text-charcoal">How Affiliate Links Work</h4>
              </div>
              <p className="text-charcoal/60 text-sm">
                When affiliates share their tracking links, the system automatically handles attribution:
              </p>

              <div className="bg-cream/50 rounded-lg p-4 border border-tan/30">
                <h5 className="font-medium text-charcoal mb-2">Tracking Flow</h5>
                <ol className="list-decimal list-inside text-sm text-charcoal/80 space-y-2">
                  <li>Affiliate creates a tracking link in their dashboard</li>
                  <li>
                    Link format: <code className="text-sage">{apiBaseUrl}/t/[shortCode]</code>
                  </li>
                  <li>Visitor clicks the link and is redirected to your site</li>
                  <li>Tracking cookies are automatically set (valid for your configured cookie duration)</li>
                  <li>When you record a sale, the system attributes it to the affiliate</li>
                </ol>
              </div>

              <div className="bg-tan/20 border border-tan/50 rounded-lg p-4">
                <h5 className="font-medium text-charcoal mb-2">Optional: URL Parameter Tracking</h5>
                <p className="text-sm text-charcoal/70 mb-3">
                  If you want to also capture <code className="text-sage">?ref=CODE</code> parameters,
                  add this snippet to your site:
                </p>
                <div className="relative">
                  <pre className="bg-charcoal rounded p-3 text-xs text-cream overflow-x-auto">
                    {trackingSnippet}
                  </pre>
                  <button
                    onClick={() => copyToClipboard(trackingSnippet, 'snippet')}
                    className="absolute top-2 right-2 p-1.5 text-cream/50 hover:text-cream hover:bg-white/10 rounded transition-colors"
                  >
                    {copiedSection === 'snippet' ? (
                      <CheckCircleIcon className="w-4 h-4 text-sage" />
                    ) : (
                      <ClipboardIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Manual API Recording */}
            <div className="space-y-4 pt-4 border-t border-tan/30">
              <div className="flex items-center gap-2">
                <ShoppingCartIcon className="w-5 h-5 text-rust" />
                <h4 className="font-medium text-charcoal">Manual API (Non-Stripe)</h4>
                <span className="px-2 py-0.5 bg-rust/10 text-rust text-xs font-medium rounded-full border border-rust/30">
                  Paid Tier
                </span>
              </div>
              <p className="text-charcoal/60 text-sm">
                If you're not using Stripe, call these endpoints from your backend to record conversions and sales.
              </p>

              {/* Important Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h5 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  Tracking Only - You Pay Commissions
                </h5>
                <p className="text-sm text-amber-700">
                  <strong>Important:</strong> When using the Manual API, VennDela only <strong>tracks</strong> commissions and payments.
                  We do not process payouts to your affiliates. You are responsible for paying your affiliates directly
                  based on the commission data we track.
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  This feature requires a <strong>paid plan</strong> since we cannot offset costs through payment processing fees.
                </p>
              </div>

              {/* Conversion API */}
              <div className="space-y-3">
                <h5 className="font-medium text-charcoal flex items-center gap-2">
                  <span className="px-2 py-1 bg-sage text-white rounded text-xs">POST</span>
                  Record Conversion (Optional)
                </h5>
                <p className="text-sm text-charcoal/60">
                  Record when a referred visitor completes a signup or registration. This creates a pending referral.
                </p>

                <div className="space-y-2">
                  <p className="text-xs text-charcoal/50">JavaScript/Node.js</p>
                  <div className="relative">
                    <pre className="bg-charcoal rounded p-3 text-xs text-cream overflow-x-auto">
                      {conversionCode}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(conversionCode, 'conversion')}
                      className="absolute top-2 right-2 p-1.5 text-cream/50 hover:text-cream hover:bg-white/10 rounded transition-colors"
                    >
                      {copiedSection === 'conversion' ? (
                        <CheckCircleIcon className="w-4 h-4 text-sage" />
                      ) : (
                        <ClipboardIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-charcoal/50">cURL</p>
                  <div className="relative">
                    <pre className="bg-charcoal rounded p-3 text-xs text-cream overflow-x-auto">
                      {curlConversionExample}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(curlConversionExample, 'curlConversion')}
                      className="absolute top-2 right-2 p-1.5 text-cream/50 hover:text-cream hover:bg-white/10 rounded transition-colors"
                    >
                      {copiedSection === 'curlConversion' ? (
                        <CheckCircleIcon className="w-4 h-4 text-sage" />
                      ) : (
                        <ClipboardIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sale API */}
              <div className="space-y-3">
                <h5 className="font-medium text-charcoal flex items-center gap-2">
                  <span className="px-2 py-1 bg-sage text-white rounded text-xs">POST</span>
                  Record Sale
                </h5>
                <p className="text-sm text-charcoal/60">
                  Record when a purchase is completed. This calculates and creates the commission based on your campaign settings.
                </p>

                <div className="space-y-2">
                  <p className="text-xs text-charcoal/50">JavaScript/Node.js</p>
                  <div className="relative">
                    <pre className="bg-charcoal rounded p-3 text-xs text-cream overflow-x-auto">
                      {saleCode}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(saleCode, 'sale')}
                      className="absolute top-2 right-2 p-1.5 text-cream/50 hover:text-cream hover:bg-white/10 rounded transition-colors"
                    >
                      {copiedSection === 'sale' ? (
                        <CheckCircleIcon className="w-4 h-4 text-sage" />
                      ) : (
                        <ClipboardIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-charcoal/50">cURL</p>
                  <div className="relative">
                    <pre className="bg-charcoal rounded p-3 text-xs text-cream overflow-x-auto">
                      {curlSaleExample}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(curlSaleExample, 'curlSale')}
                      className="absolute top-2 right-2 p-1.5 text-cream/50 hover:text-cream hover:bg-white/10 rounded transition-colors"
                    >
                      {copiedSection === 'curlSale' ? (
                        <CheckCircleIcon className="w-4 h-4 text-sage" />
                      ) : (
                        <ClipboardIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Parameters Reference */}
              <div className="bg-cream/50 rounded-lg p-4 border border-tan/30">
                <h5 className="font-medium text-charcoal mb-3">API Parameters Reference</h5>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-charcoal/60 border-b border-tan/30">
                      <th className="pb-2">Parameter</th>
                      <th className="pb-2">Type</th>
                      <th className="pb-2">Required</th>
                      <th className="pb-2">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-charcoal/80">
                    <tr className="border-b border-tan/20">
                      <td className="py-2"><code className="text-sage">orderId</code></td>
                      <td>string</td>
                      <td>Yes (sale)</td>
                      <td>Your unique order identifier</td>
                    </tr>
                    <tr className="border-b border-tan/20">
                      <td className="py-2"><code className="text-sage">amount</code></td>
                      <td>number</td>
                      <td>Yes (sale)</td>
                      <td>Order amount in cents (e.g., 9999 = $99.99)</td>
                    </tr>
                    <tr className="border-b border-tan/20">
                      <td className="py-2"><code className="text-sage">customerId</code></td>
                      <td>string</td>
                      <td>Yes</td>
                      <td>Your unique customer identifier</td>
                    </tr>
                    <tr className="border-b border-tan/20">
                      <td className="py-2"><code className="text-sage">customerEmail</code></td>
                      <td>string</td>
                      <td>No</td>
                      <td>Customer email address</td>
                    </tr>
                    <tr className="border-b border-tan/20">
                      <td className="py-2"><code className="text-sage">visitorId</code></td>
                      <td>string</td>
                      <td>No</td>
                      <td>From VennDela_vid cookie (improves attribution)</td>
                    </tr>
                    <tr>
                      <td className="py-2"><code className="text-sage">campaignId</code></td>
                      <td>string</td>
                      <td>No</td>
                      <td>Override campaign for this sale</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stripe Advanced Details */}
            <div className="space-y-4 pt-4 border-t border-tan/30">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#635BFF]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
                <h4 className="font-medium text-charcoal">Stripe Attribution Details</h4>
              </div>

              {/* How Attribution Works */}
              <div className="bg-cream/50 rounded-lg p-4 border border-tan/30">
                <h5 className="font-medium text-charcoal mb-3">How Attribution Works</h5>
                <p className="text-sm text-charcoal/70 mb-3">
                  When we receive a payment webhook, we attribute it using this priority:
                </p>
                <ol className="list-decimal list-inside text-sm text-charcoal/80 space-y-1">
                  <li><code className="text-sage">VennDela_vid</code> in metadata → matches click records</li>
                  <li><code className="text-sage">VennDela_aid</code> in metadata → direct affiliate match</li>
                  <li>Existing referral by Stripe Customer ID</li>
                  <li>Existing referral by customer email</li>
                </ol>
                <p className="text-sm text-charcoal/70 mt-3">
                  <strong>Important:</strong> Options 3 and 4 only work if a referral was created during the initial purchase.
                  For subscription upgrades/renewals to be tracked, the first payment must include the tracking metadata.
                </p>
              </div>

              {/* Subscription Upgrades Warning */}
              <div className="bg-tan/20 border border-tan/50 rounded-lg p-4">
                <h5 className="font-medium text-charcoal mb-2">Subscription Upgrades & Renewals</h5>
                <p className="text-sm text-charcoal/70">
                  Once a customer is linked to an affiliate (via their first tracked payment), all future payments
                  from that customer will automatically generate commissions—including upgrades and renewals.
                </p>
                <p className="text-sm text-charcoal/70 mt-2">
                  <strong>If upgrades aren't being tracked:</strong> The initial subscription likely wasn't created with
                  VennDela metadata. You can manually link the customer by calling the conversion API with their
                  Stripe Customer ID (<code className="text-sage">cus_xxx</code>).
                </p>
              </div>

              {/* Direct subscription creation */}
              <div className="space-y-2">
                <p className="text-xs text-charcoal/50 font-medium">For direct subscription creation (not using Checkout):</p>
                <div className="relative">
                  <pre className="bg-charcoal rounded p-3 text-xs text-cream overflow-x-auto">
{`// Creating subscription directly
const subscription = await stripe.subscriptions.create({
  customer: customerId,
  items: [{ price: priceId }],

  // REQUIRED: Pass VennDela tracking data
  metadata: {
    VennDela_vid: visitorId || '',
    VennDela_aid: affiliateId || '',
  },
});`}
                  </pre>
                </div>
              </div>

              {/* Metadata Keys Reference */}
              <div className="bg-cream/50 rounded-lg p-4 border border-tan/30">
                <h5 className="font-medium text-charcoal mb-3">Accepted Metadata Keys</h5>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-charcoal/60 border-b border-tan/30">
                      <th className="pb-2">Key</th>
                      <th className="pb-2">Alternatives</th>
                      <th className="pb-2">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-charcoal/80">
                    <tr className="border-b border-tan/20">
                      <td className="py-2"><code className="text-sage">VennDela_vid</code></td>
                      <td><code className="text-sage text-xs">venndela_vid</code>, <code className="text-sage text-xs">visitor_id</code></td>
                      <td>Visitor ID from tracking cookie</td>
                    </tr>
                    <tr>
                      <td className="py-2"><code className="text-sage">VennDela_aid</code></td>
                      <td><code className="text-sage text-xs">venndela_aid</code>, <code className="text-sage text-xs">affiliate_id</code></td>
                      <td>Affiliate ID for direct attribution</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help */}
      <div className="rounded-xl bg-rust/10 border border-rust/30 shadow-sm">
        <div className="p-6">
          <h4 className="font-medium text-charcoal mb-2">Need Help?</h4>
          <p className="text-sm text-charcoal/70">
            If you need assistance with integration, check our documentation or contact support.
            Make sure to test your integration in a development environment before going live.
          </p>
        </div>
      </div>
    </div>
  );
}
